// 德州扑克 in the 牌桌 panel (public/cards.js mounts it; the rules: src/cards/holdem.ts). The felt with every seat
// (chips, bets, the dealer button, whose turn with its clock, cards face down or shown at a showdown), the board and
// the pot, my two cards big under it, and my moves: 弃牌, 过牌 / 跟注, 加注 (a slider and 最小 / 半池 / 一池 / 最大),
// 全下. A log of the hand's moves under it. Only what the office sent me is drawn: my own cards, others' at a showdown.
import { CARD_W, CARD_H, drawCard, drawFelt, seatXY, nameBox, boxAt, chip, outlined, sized, el, BOX_H, seatName } from '/cards-common.js';
import { t, tr, has } from '/i18n.js';

const W = 400, H = 290;
/** A street (0 翻牌前 … 3 河牌, the office's street number) in words. */
const street = (i) => (i >= 0 && i <= 3 ? t(`holdem.street.${i}`) : '');
/** The words for a seat's state under its chips, and the raise button's, by state (the code picks the state). */
const TAG = { folded: 'holdem.tag.folded', allin: 'holdem.tag.allin', next: 'holdem.tag.next' };
const RAISE_BTN = { open: 'holdem.raise.hide', raise: 'holdem.btn.raise', bet: 'holdem.btn.bet' };
/** A hand rank from its number (rk: 0 high card … 9 royal flush; short: the seat box's shorter words); a hand from an
 *  older office: its words (`rank`). */
const rankText = (x, short = false) => (Number.isInteger(x?.rk) ? t(`holdem.rank${short ? 'Short' : ''}.${x.rk}`) : x?.rank ?? '');
/** A move in the log from its code (l.k: fold, call, raise …); an older office's: its words (l.a). */
const actText = (l) => (l.k && has(`holdem.act.${l.k}`) ? t(`holdem.act.${l.k}`) : l.a);

export default {
  kind: 'holdem', get name() { return t('holdem.name'); }, icon: 'cards', seats: 8, get blurb() { return t('holdem.blurb'); },
  /** Build the game's part of the panel in `root`; api: see public/cards.js (send, mySeat, myTurn, …). */
  mount(root, api) {
    let raising = false, raiseTo = 0, st = null;
    const canvas = el('canvas', 'ctfelt'), wrap = el('div', 'ctfeltwrap'); wrap.append(canvas);
    const hand = el('div', 'cthand'), handCanvas = el('canvas', 'cthandc'), handText = el('div', 'cthandtext'); hand.append(handCanvas, handText);
    const acts = el('div', 'gmbtns ctacts');
    const foldBtn = el('button'), callBtn = el('button', 'gmprimary'), raiseBtn = el('button'), allinBtn = el('button', 'ctallin');
    acts.append(foldBtn, callBtn, raiseBtn, allinBtn);
    const raiseRow = el('div', 'ctraise'), slider = el('input'), raiseAmt = el('b', 'ctraiseamt'), presets = el('div', 'gmpick'), raiseGo = el('button', 'gmprimary');
    slider.type = 'range'; slider.className = 'ctslider';
    const potAfterCall = () => (v().pot ?? 0) + (v().you?.toCall ?? 0);
    const preset = (key, f) => { const b = tr(el('button'), key); b.onclick = () => setRaise(f()); return b; };
    presets.append(preset('holdem.preset.min', () => v().you.minTo), preset('holdem.preset.half', () => v().bet + Math.round(potAfterCall() / 2)), preset('holdem.preset.pot', () => v().bet + potAfterCall()), preset('holdem.preset.max', () => v().you.maxTo));
    raiseRow.append(presets, slider, raiseAmt, raiseGo);
    const me2 = el('div', 'ctme'), log = el('div', 'ctlog');
    root.append(wrap, hand, acts, raiseRow, me2, log);

    const v = () => st?.view ?? {};
    const act = (move) => { api.send(move); raising = false; };
    foldBtn.onclick = () => (v().you?.toCall ? act('fold') : api.twoStep('fold', () => act('fold'))());
    callBtn.onclick = () => act(v().you?.toCall ? 'call' : 'check');
    raiseBtn.onclick = () => { raising = !raising; if (raising) setRaise(v().you.minTo); render(st); };
    allinBtn.onclick = api.twoStep('allin', () => act('allin'));
    raiseGo.onclick = () => act(`raise ${raiseTo}`);
    slider.oninput = () => setRaise(+slider.value);
    function setRaise(x) {
      const y = v().you; if (!y) return;
      raiseTo = x >= y.maxTo ? y.maxTo : Math.max(y.minTo, Math.min(y.maxTo, Math.round(x / 10) * 10 || y.minTo));
      slider.min = y.minTo; slider.max = y.maxTo; slider.step = 10; slider.value = raiseTo;
      raiseAmt.textContent = raiseTo === y.maxTo ? t('holdem.raise.allin', { n: raiseTo }) : String(raiseTo);
      raiseGo.textContent = t(v().bet ? 'holdem.raise.to' : 'holdem.bet.amt', { n: raiseTo });
    }
    const bottom = () => (api.mySeat() >= 0 ? api.mySeat() : 0);
    const at = (i) => seatXY(i, st.seats.length, bottom(), W, H);
    canvas.addEventListener('click', (e) => { if (!st) return; const i = boxAt(canvas, e, W, H, st.seats.length, at); if (i >= 0 && !st.seats[i] && api.mySeat() < 0) api.sitAt(i); });
    canvas.addEventListener('pointermove', (e) => { if (!st) return; const i = boxAt(canvas, e, W, H, st.seats.length, at); canvas.style.cursor = i >= 0 && !st.seats[i] && api.mySeat() < 0 ? 'pointer' : ''; });

    /** At a showdown: the cards that made the winning hands (the rest are dimmed). */
    function winning() {
      const res = v().result, out = new Set();
      if (res && !res.folded) for (const pot of res.pots) for (const w of pot.winners) for (const c of res.shown?.[w]?.best ?? []) out.add(c);
      return out;
    }
    function draw() {
      const g = sized(canvas, W, H);
      drawFelt(g, W, H);
      if (!st) return;
      const V = v(), n = st.seats.length, players = V.players ?? [], res = V.result, mine = api.mySeat(), win = winning();
      // the board and the pot
      const board = V.board ?? [], k = 1.5, cw = CARD_W * k, gap = 4, bx = W / 2 - (5 * cw + 4 * gap) / 2, by = H / 2 - (CARD_H * k) / 2 - 6;
      for (let i = 0; i < 5; i++) {
        const x = bx + i * (cw + gap);
        if (board[i]) drawCard(g, board[i], x, by, k, !!res && !res.folded && !win.has(board[i]));
        else { g.strokeStyle = 'rgba(255,248,231,.35)'; g.lineWidth = 1.5; g.strokeRect(x + 1, by + 1, cw - 2, CARD_H * k - 2); }
      }
      if (V.pot) outlined(g, t('holdem.pot', { n: V.pot }), W / 2, by + CARD_H * k + 12);
      for (let i = 0; i < n; i++) {
        const [x, y] = at(i), s = st.seats[i], p = players[i], won = !!res && res.won?.[i] > 0, sh = res?.shown?.[i];
        // their cards: above the name box in the bottom half, below it in the top half, a little outward (clear of the board)
        const below = y <= H / 2 + 1, cx = x - (W / 2 - x) * 0.15;
        if (p && p.cards && i !== mine) {
          if (p.hole) p.hole.forEach((c, j) => drawCard(g, c, cx - CARD_W + j * (CARD_W + 2) - 1, below ? y + BOX_H / 2 + 3 : y - BOX_H / 2 - 3 - CARD_H, 1, !!res && !win.has(c) && !won));
          else for (let j = 0; j < p.cards; j++) { g.save(); g.translate(cx + j * 8 - 4, below ? y + BOX_H / 2 + 11 : y - BOX_H / 2 - 11); g.rotate((j - 0.5) * 0.2); drawCard(g, 'back', -CARD_W / 4, -CARD_H / 4, 0.5); g.restore(); }
        }
        if (p && p.bet) chip(g, x + (W / 2 - x) * 0.55 - 8, y + (H / 2 - y) * 0.6, p.bet);
        const tagId = p?.folded ? 'folded' : p?.allin ? 'allin' : p && !p.in && (V.live || !V.hand) ? 'next' : null, tag = tagId ? t(TAG[tagId]) : '';
        const acting = st.actors?.includes(i);
        nameBox(g, x, y, { name: seatName(s, i), empty: mine < 0 ? t('holdem.seat.sitHere') : t('cards.seat.empty'), line: p ? (sh || tag ? t('holdem.seat.line', { stack: p.stack, tag: sh ? rankText(sh, true) : tag }) : String(p.stack)) : '',
          mine: i === mine, acting, won, dim: !!p && (p.folded || (!p.in && V.live)), badge: i === V.button && V.hand ? 'D' : null,
          clock: acting && st.moveMs ? (api.deadline() - Date.now()) / st.moveMs : null });
      }
    }
    function drawHand() {
      const y = v().you, hole = y?.hole ?? [], k = 2, p = v().players?.[api.mySeat()];
      const w = hole.length ? hole.length * CARD_W * k + (hole.length - 1) * 6 : 1, h = CARD_H * k;
      handCanvas.style.width = w + 'px'; handCanvas.style.height = h + 'px';
      const g = sized(handCanvas, w, h);
      hole.forEach((c, i) => drawCard(g, c, i * (CARD_W * k + 6), 0, k, !!p?.folded));
      handText.textContent = hole.length ? (p?.folded ? t('holdem.hand.folded') : y.rank ? t('holdem.hand.rank', { rank: rankText(y) }) : t('holdem.hand.mine')) : '';
    }
    function logLines() {
      const lines = []; let at = 0;
      for (const l of v().log ?? []) {
        if (l.street !== at) { at = l.street; lines.push({ t: street(at), street: true }); }
        // a raise or a bet says what it went to
        const name = api.name(l.seat), a = actText(l);
        lines.push({ t: l.k === 'raise' || l.k === 'bet' ? t('holdem.log.to', { name, a, amt: l.amt }) : t(l.amt ? 'holdem.log.amt' : 'holdem.log', { name, a, amt: l.amt }) });
      }
      return lines.slice(-7);
    }
    function render(next) {
      if (next !== st) { if (st && (st.round !== next?.round || st.actors?.join() !== next?.actors?.join())) raising = false; st = next; }
      if (!st) return;
      const V = v(), seat = api.mySeat(), mine = api.myTurn(), y = V.you;
      acts.style.display = mine ? '' : 'none';
      if (mine && y) {
        foldBtn.textContent = t(api.confirming('fold') ? 'holdem.fold.confirm' : 'holdem.btn.fold');
        callBtn.textContent = y.toCall ? t('holdem.call.amt', { n: y.toCall }) : t('holdem.btn.check');
        raiseBtn.style.display = y.canRaise && y.minTo < y.maxTo ? '' : 'none';
        raiseBtn.textContent = t(RAISE_BTN[raising ? 'open' : V.bet ? 'raise' : 'bet']);
        allinBtn.style.display = y.canRaise ? '' : 'none';
        allinBtn.textContent = t(api.confirming('allin') ? 'holdem.allin.confirm' : 'holdem.allin.amt', { n: y.maxTo });
        if (!y.canRaise) raising = false;
      } else raising = false;
      if (raising && y) setRaise(raiseTo); // (its words again: a language change)
      raiseRow.style.display = mine && raising ? '' : 'none';
      hand.style.display = seat >= 0 && y?.hole?.length ? '' : 'none';
      const p = V.players?.[seat];
      me2.style.display = seat >= 0 && p ? '' : 'none';
      if (p) me2.textContent = t('holdem.me', { stack: p.stack, net: p.net > 0 ? 'up' : p.net < 0 ? 'down' : 'even', n: Math.abs(p.net), rebuys: Math.max(0, (p.buys ?? 1) - 1) });
      const ll = logLines();
      log.replaceChildren(...ll.map((l) => el('div', l.street ? 'ctlogst' : '', l.t)));
      log.style.display = ll.length ? '' : 'none';
      draw(); drawHand();
    }
    return {
      render,
      /** Every 250 ms while shown: the clocks. */
      tick: draw,
      /** The line over the felt (null: the panel's own, for waiting and between hands). */
      status(s) {
        const V = s.view ?? {};
        if (s.phase === 'over' && V.result) {
          const res = V.result, lines = [];
          for (const pot of res.pots) {
            if (pot.seats.length === 1 && res.pots.length > 1 && !res.folded) continue; // only their own bet back
            const names = pot.winners.map(api.name).join(t('cards.listSep')), sh = res.shown?.[pot.winners[0]], rank = sh && rankText(sh);
            lines.push(rank ? t('holdem.won.rank', { names, amt: pot.amt, rank }) : t(res.folded ? 'holdem.won.folded' : 'holdem.won.plain', { names, amt: pot.amt }));
          }
          return lines.join(t('holdem.won.sep')) || null;
        }
        if (s.phase !== 'playing') return null;
        if (api.myTurn()) return V.you?.toCall ? t('holdem.turn.call', { n: V.you.toCall }) : t('holdem.turn.check');
        return t('holdem.waitFor', { street: Number.isInteger(V.street) ? street(V.street) : V.streetName ?? '', name: api.name(s.actors?.[0]) });
      },
      /** Standing up now costs something: what to ask first (null: just go). */
      leaveWarn(s) { const p = s.view?.players?.[api.mySeat()]; return s.phase === 'playing' && p?.in && !p.folded ? t('holdem.leave.warn') : null; },
    };
  },
};
